<svelte:options runes={false} />
<script>
  import { createEventDispatcher } from 'svelte';
  import { strategyParams, policyFlagsFor } from '../../lib/strategies.js';

  const dispatch = createEventDispatcher();

  // One-way input map of operator overrides from the form: { paramKey: value }. Empty/unset entries mean
  // "use the strategy default" (the request omits them, preserving byte-identical backend behavior). We keep
  // a LOCAL working copy (`vals`) and report edits up via a `change` event rather than a two-way `bind:`.
  // (A bindable prop on this component interferes with the reactivity of the sibling-driving `strategyId`
  // prop in Svelte 5 — the event approach keeps both props cleanly reactive.)
  export let values = {};
  // The selected StrategyType id — drives which strategy-specific params show.
  export let strategyId;
  // Server-declared frozen parameters for this strategy: [{ name, display, reason }] (ADR-0024 §2).
  // These render DISABLED SHOWING THEIR FORCED VALUE rather than hidden — a greyed-out box that reads
  // "Off (long-only)" tells the operator what the strategy does; a missing field teaches nothing and
  // invites the same question next week.
  export let lockedParams = [];

  $: lockedByKey = Object.fromEntries((lockedParams ?? []).map((p) => [p.name, p]));
  $: isLocked = (key) => Object.prototype.hasOwnProperty.call(lockedByKey, key);

  // Local working copy. Re-sync only when the form pushes a NEW object identity (reset, applyParams, or
  // strategy-switch clear) — not on every keystroke (that would clobber the field being typed).
  let vals = { ...values };
  let lastValuesRef = values;
  $: if (values !== lastValuesRef) {
    lastValuesRef = values;
    vals = { ...values };
  }

  function edit(key, value) {
    vals = { ...vals, [key]: value };
    dispatch('change', vals);
  }

  // Per-field validation messages, surfaced inline. Reported to the form via a `validity` event (carrying
  // the count of invalid fields) rather than a bound-reassigned prop, so the form's reactive submit-gate
  // updates reliably across Svelte versions.
  let errors = {};

  // Strategy-specific numeric knobs + inline flags (always visible for the selected strategy).
  $: params = strategyParams(strategyId);

  // Advanced policy section is collapsed by default — it applies to every strategy and would clutter the
  // common path. A small badge shows how many policy overrides are active so a collapsed section never
  // hides a meaningful change.
  let policyOpen = false;
  // Frozen-spec strategies (Shishito) hide flags the backend boundary rejects for them.
  $: applicablePolicyFlags = policyFlagsFor(strategyId);
  // Locked flags are DISPLAYED but never counted as operator overrides — they aren't ones.
  $: activePolicyCount = applicablePolicyFlags
    .filter((f) => !isLocked(f.key) && isActive(f, vals[f.key])).length;

  // Drop any carried-over value for a now-locked key: switching to a frozen strategy must not leave a
  // stale override in the submitted tuning, which the boundary would 422 (ADR-0024 §3). The UI lock is
  // an affordance; this is what keeps the REQUEST honest.
  $: clearLockedValues(lockedByKey);
  function clearLockedValues(locked) {
    const stale = Object.keys(locked).filter((k) => vals[k] !== undefined && vals[k] !== '');
    if (stale.length === 0) return;
    const next = { ...vals };
    for (const k of stale) next[k] = '';
    vals = next;
    dispatch('change', vals);
  }

  function isActive(flag, v) {
    if (v === undefined || v === null || v === '') return false;
    if (flag.kind === 'bool') return v === true;
    return true; // enum with a non-empty selection
  }

  // ---- Inline validation mirrors the backend StrategyTuningValidator (positive periods, list bounds,
  //      short<long pairs) so an invalid knob is caught before the round trip. ----
  $: errors = validate(params, vals);
  // Report the invalid-field count up so the form can gate submit (event, not bound prop — see above).
  $: dispatch('validity', Object.keys(errors).length);

  function validate(activeParams, current) {
    const e = {};
    for (const p of activeParams) {
      const raw = current[p.key];
      if (raw === '' || raw === undefined || raw === null || raw === true || raw === false) continue;

      if (p.kind === 'int') {
        const n = Number(raw);
        if (Number.isNaN(n) || !Number.isInteger(n)) e[p.key] = 'Enter a whole number.';
        else if (p.min !== undefined && n < p.min) e[p.key] = `Must be at least ${p.min}.`;
        else if (p.max !== undefined && n > p.max) e[p.key] = `Must be at most ${p.max.toLocaleString()}.`;
      } else if (p.kind === 'intlist') {
        const parts = String(raw).split(',').map((s) => s.trim()).filter(Boolean);
        const nums = parts.map(Number);
        if (nums.some((n) => Number.isNaN(n) || !Number.isInteger(n) || n <= 0)) {
          e[p.key] = 'Comma-separated positive whole numbers.';
        } else if (nums.length > 64) {
          e[p.key] = 'At most 64 values.';
        }
      } else if (p.kind === 'pairlist') {
        const parts = String(raw).split(',').map((s) => s.trim()).filter(Boolean);
        if (parts.length > 64) e[p.key] = 'At most 64 pairs.';
        else {
          for (const part of parts) {
            const pair = part.split(':').map((x) => Number(x.trim()));
            if (pair.length !== 2 || pair.some((n) => Number.isNaN(n) || n <= 0)) { // not-a-venue-id: a [lo, hi] pair
              e[p.key] = 'Use short:long pairs, e.g. 2:8, 4:16.';
              break;
            }
            if (pair[0] >= pair[1]) {
              e[p.key] = 'Each pair needs short < long.';
              break;
            }
          }
        }
      }
    }
    return e;
  }

  function resetParam(key) {
    edit(key, '');
  }
</script>

<div class="strategy-params">
  {#if params.length > 0}
    <div class="sp-section">
      <div class="sp-section-head">
        <span class="sp-section-title">Strategy parameters</span>
        <span class="sp-section-sub">Leave blank to use the strategy’s tuned default</span>
      </div>

      <div class="sp-grid">
        {#each params as p (p.key)}
          {#if p.kind === 'bool'}
            <label class="sp-toggle" title={p.hint}>
              <input type="checkbox" checked={vals[p.key] === true}
                on:change={(e) => edit(p.key, e.currentTarget.checked)} />
              <span class="sp-toggle-track"><span class="sp-toggle-thumb"></span></span>
              <span class="sp-toggle-label">
                {p.label}
                <span class="sp-toggle-hint">{p.hint}</span>
              </span>
            </label>
          {:else}
            <div class="sp-field" class:has-value={vals[p.key] !== '' && vals[p.key] != null}>
              <div class="sp-field-head">
                <label class="sp-label" for={`sp-${p.key}`}>{p.label}</label>
                {#if vals[p.key] !== '' && vals[p.key] != null}
                  <button type="button" class="sp-reset" on:click={() => resetParam(p.key)}
                    title="Reset to default">default</button>
                {/if}
              </div>
              <input
                id={`sp-${p.key}`}
                class="sp-input"
                type={p.kind === 'int' ? 'number' : 'text'}
                inputmode={p.kind === 'int' ? 'numeric' : undefined}
                min={p.min}
                max={p.max}
                placeholder={p.placeholder ?? 'default'}
                value={vals[p.key] ?? ''}
                on:input={(e) => edit(p.key, e.currentTarget.value)}
                aria-invalid={!!errors[p.key]}
                aria-describedby={`sp-${p.key}-hint`}
              />
              <span id={`sp-${p.key}-hint`} class="sp-hint">{p.hint}</span>
              {#if errors[p.key]}<span class="sp-error">{errors[p.key]}</span>{/if}
            </div>
          {/if}
        {/each}
      </div>
    </div>
  {:else}
    <div class="sp-none">This strategy runs with no tunable parameters — it uses its built-in logic.</div>
  {/if}

  <!-- Advanced policy: collapsed by default, applies to every strategy. -->
  <div class="sp-section sp-policy" class:open={policyOpen}>
    <button type="button" class="sp-policy-toggle" aria-expanded={policyOpen}
      on:click={() => (policyOpen = !policyOpen)}>
      <span class="sp-chevron" aria-hidden="true">▸</span>
      <span class="sp-policy-title">Advanced policy</span>
      {#if activePolicyCount > 0}
        <span class="sp-badge">{activePolicyCount} active</span>
      {/if}
      <span class="sp-policy-hint">Risk &amp; execution overrides</span>
    </button>

    {#if policyOpen}
      <div class="sp-grid sp-policy-grid">
        {#each applicablePolicyFlags as f (f.key)}
          {#if isLocked(f.key)}
            <!-- Frozen by the strategy: shown, disabled, carrying the value the strategy forces and
                 the reason it is not editable (ADR-0024 §2). -->
            <div class="sp-field sp-locked" data-testid={`locked-${f.key}`}>
              <div class="sp-field-head">
                <label class="sp-label" for={`sp-${f.key}`}>{f.label}</label>
                <span class="sp-lock-badge" title={lockedByKey[f.key].reason}>locked</span>
              </div>
              <input id={`sp-${f.key}`} class="sp-input sp-locked-input" type="text"
                value={lockedByKey[f.key].display} disabled readonly
                aria-describedby={`sp-${f.key}-lock`} />
              <span id={`sp-${f.key}-lock`} class="sp-hint">Set by the strategy — {f.hint}</span>
            </div>
          {:else if f.kind === 'bool'}
            <label class="sp-toggle" title={f.hint}>
              <input type="checkbox" checked={vals[f.key] === true}
                on:change={(e) => edit(f.key, e.currentTarget.checked)} />
              <span class="sp-toggle-track"><span class="sp-toggle-thumb"></span></span>
              <span class="sp-toggle-label">
                {f.label}
                <span class="sp-toggle-hint">{f.hint}</span>
              </span>
            </label>
          {:else if f.kind === 'enum'}
            <div class="sp-field">
              <label class="sp-label" for={`sp-${f.key}`}>{f.label}</label>
              <select id={`sp-${f.key}`} class="sp-input sp-select"
                value={vals[f.key] ?? ''}
                on:change={(e) => edit(f.key, e.currentTarget.value === '' ? '' : Number(e.currentTarget.value))}>
                <option value="">Strategy default</option>
                {#each f.options as o}
                  <option value={o.value}>{o.label}</option>
                {/each}
              </select>
              <span class="sp-hint">{f.hint}</span>
            </div>
          {/if}
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  @import './StrategyParameters.css';
</style>